import type { ToolSummary } from '../api/TraceabilityResponseType';

/** A reference used by the company's tools, with the number of tools carrying it. */
export type ReferenceOption = { reference: string; toolCount: number };

type AliasFor = (reference: string) => string | undefined;

/** Comparison key: case, spaces and `-` `_` `.` `/` do not count ("m 3520" ≈ "M3520"). */
export function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[\s\-_./]/g, '');
}

/** The company's references, most used first. */
export function referenceOptions(summaries: readonly ToolSummary[]): ReferenceOption[] {
  const counts = new Map<string, number>();
  for (const summary of summaries) {
    const reference = summary.reference?.trim();
    if (reference) counts.set(reference, (counts.get(reference) ?? 0) + 1);
  }
  return [...counts]
    .map(([reference, toolCount]) => ({ reference, toolCount }))
    .sort((a, b) => b.toolCount - a.toolCount || a.reference.localeCompare(b.reference));
}

/** References whose name or alias contains the query; prefix matches first, then most used. */
export function filterReferenceOptions(
  query: string,
  options: readonly ReferenceOption[],
  aliasFor: AliasFor
): ReferenceOption[] {
  const key = normalizeKey(query.trim());
  if (!key) return [...options];
  const startsWith = (option: ReferenceOption) => normalizeKey(option.reference).startsWith(key);
  return options
    .filter(
      (option) =>
        normalizeKey(option.reference).includes(key) ||
        normalizeKey(aliasFor(option.reference) ?? '').includes(key)
    )
    .sort(
      (a, b) =>
        Number(startsWith(b)) - Number(startsWith(a)) ||
        b.toolCount - a.toolCount ||
        a.reference.localeCompare(b.reference)
    );
}

export function findReference(
  value: string,
  options: readonly ReferenceOption[]
): ReferenceOption | null {
  const reference = value.trim();
  return options.find((option) => option.reference === reference) ?? null;
}

/** An existing reference that differs from the value only by case or separators. */
export function findNearReference(
  value: string,
  options: readonly ReferenceOption[]
): ReferenceOption | null {
  const reference = value.trim();
  const key = normalizeKey(reference);
  if (!key) return null;
  return (
    options.find(
      (option) => option.reference !== reference && normalizeKey(option.reference) === key
    ) ?? null
  );
}

/** Tools whose ID, reference or alias contains the query; ID prefix first, then most recently traced. */
export function filterTools(
  query: string,
  summaries: readonly ToolSummary[],
  aliasFor: AliasFor
): ToolSummary[] {
  const key = normalizeKey(query.trim());
  const idStartsWith = (summary: ToolSummary) => normalizeKey(summary.toolId).startsWith(key);
  return summaries
    .filter((summary) => {
      if (!key) return true;
      const reference = summary.reference ?? '';
      return (
        normalizeKey(summary.toolId).includes(key) ||
        normalizeKey(reference).includes(key) ||
        normalizeKey(aliasFor(reference) ?? '').includes(key)
      );
    })
    .sort(
      (a, b) =>
        Number(idStartsWith(b)) - Number(idStartsWith(a)) ||
        (b.lastTraceAt ?? '').localeCompare(a.lastTraceAt ?? '') ||
        a.toolId.localeCompare(b.toolId)
    );
}

export function findTool(value: string, summaries: readonly ToolSummary[]): ToolSummary | null {
  const toolId = value.trim();
  return summaries.find((summary) => summary.toolId === toolId) ?? null;
}

/** An existing tool whose ID differs from the value only by case or separators. */
export function findNearTool(value: string, summaries: readonly ToolSummary[]): ToolSummary | null {
  const toolId = value.trim();
  const key = normalizeKey(toolId);
  if (!key) return null;
  return (
    summaries.find(
      (summary) => summary.toolId !== toolId && normalizeKey(summary.toolId) === key
    ) ?? null
  );
}

const ID_WITH_NUMBER = /^(.*\D)(\d+)$/;

/**
 * The "prefix + number" family the typed ID belongs to: the single family of the IDs starting
 * with the text (`HEARC-102` → `HEARC-102-`), or, when no ID starts with it, the typed ID's own
 * prefix if that family exists (`HEARC-1520-0009` → `HEARC-1520-`). Null when ambiguous.
 */
function familyPrefix(value: string, summaries: readonly ToolSummary[]): string | null {
  const typed = value.trim();
  if (!typed) return null;
  const lower = typed.toLowerCase();
  const prefixes = new Set<string>();
  for (const { toolId } of summaries) {
    const match = ID_WITH_NUMBER.exec(toolId);
    if (match && toolId.toLowerCase().startsWith(lower)) prefixes.add(match[1]);
  }
  if (prefixes.size === 1) return [...prefixes][0];
  if (prefixes.size > 1) return null;
  const own = ID_WITH_NUMBER.exec(typed);
  if (!own) return null;
  const ownPrefix = own[1].toLowerCase();
  for (const { toolId } of summaries) {
    const match = ID_WITH_NUMBER.exec(toolId);
    if (match && match[1].toLowerCase() === ownPrefix) return match[1];
  }
  return null;
}

/**
 * Existing IDs that look like the typed one: those containing it plus the members of its ID
 * family (`HEARC-1520-0001` also lists the other HEARC-1520-… tools). Sorted in natural order.
 */
export function similarToolIds(value: string, summaries: readonly ToolSummary[]): ToolSummary[] {
  const key = normalizeKey(value.trim());
  if (key.length < 2) return [];
  const prefix = familyPrefix(value, summaries);
  return summaries
    .filter((summary) => {
      if (normalizeKey(summary.toolId).includes(key)) return true;
      const match = prefix === null ? null : ID_WITH_NUMBER.exec(summary.toolId);
      return match !== null && match[1] === prefix;
    })
    .sort((a, b) => a.toolId.localeCompare(b.toolId, undefined, { numeric: true }));
}

/**
 * The number after the highest one of the typed ID's family (gaps are never reused: a gap can
 * be a retired tool). `HEARC-102` with 0001, 0003–0005 → `HEARC-102-0006`.
 */
export function nextFreeToolId(value: string, summaries: readonly ToolSummary[]): string | null {
  const prefix = familyPrefix(value, summaries);
  if (prefix === null) return null;
  let highest = 0;
  let width = 0;
  for (const { toolId } of summaries) {
    const match = ID_WITH_NUMBER.exec(toolId);
    if (match && match[1] === prefix) {
      highest = Math.max(highest, Number(match[2]));
      width = Math.max(width, match[2].length);
    }
  }
  return prefix + String(highest + 1).padStart(width, '0');
}
